import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { usePermissions } from "../hooks/usePermissions";
import { ROLE_LABELS } from "../lib/permissions";
import {
    ShieldCheck, Users, Mail, Settings, Activity, Plus, X, Loader2, Copy,
    Check, Trash2, KeyRound, AlertTriangle, Power, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const TABS = [
    { id: "users", label: "Users", icon: Users },
    { id: "invitations", label: "Invitations", icon: Mail },
    { id: "roles", label: "Roles & Permissions", icon: Settings },
    { id: "health", label: "System Health", icon: Activity },
];

export default function AdminConsole() {
    const perm = usePermissions();
    const [tab, setTab] = useState("users");

    if (!perm.isAdminFamily) {
        return (
            <div className="border border-red-500/40 bg-red-500/5 p-6" data-testid="admin-restricted">
                <div className="overline text-red-400">Restricted</div>
                <div className="heading-display text-xl mt-2">Admin Console requires Admin or Super Admin role.</div>
                <Link to="/dashboard" className="cerb-btn-secondary mt-4 inline-flex">← Back to Dashboard</Link>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-console-page">
            <div>
                <div className="overline">System / Administration</div>
                <h1 className="heading-display text-4xl mt-1 flex items-center gap-3">
                    <ShieldCheck className="w-8 h-8 text-signal" />
                    Admin Console
                </h1>
                <p className="text-snow-dim text-sm mt-2 max-w-2xl">
                    Manage identities, roles, invitations, and monitor system health.
                    {perm.isSuperAdmin && <span className="ml-2 inline-flex items-center border bg-signal text-jet font-bold px-2 py-0.5 text-[10px] uppercase tracking-[0.2em]" data-testid="super-admin-badge-self">Super Admin</span>}
                </p>
            </div>

            <div className="border-b border-hairline flex items-center gap-1" data-testid="admin-tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] border-b-2 transition-colors flex items-center gap-2 ${
                                tab === t.id ? "border-signal text-signal" : "border-transparent text-snow-dim hover:text-snow"
                            }`}
                        >
                            <Icon className="w-3 h-3" />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "users" && <UsersTab perm={perm} />}
            {tab === "invitations" && <InvitationsTab />}
            {tab === "roles" && <RolesTab />}
            {tab === "health" && <HealthTab />}
        </div>
    );
}

// ===== USERS =====
function UsersTab({ perm }) {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resetTarget, setResetTarget] = useState(null);
    const [tempPwd, setTempPwd] = useState(null);
    const [busy, setBusy] = useState(null);
    const [statusFilter, setStatusFilter] = useState("All");

    const refresh = async () => {
        try {
            const [u, r] = await Promise.all([
                api.get("/admin/users").then((r) => r.data),
                api.get("/admin/roles").then((r) => r.data).catch(() => []),
            ]);
            setUsers(u);
            setRoles(r);
        } catch (err) {
            if (err?.response?.status !== 401) toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const filtered = useMemo(() => {
        if (statusFilter === "All") return users.filter((u) => !u.is_deleted);
        if (statusFilter === "Inactive") return users.filter((u) => u.is_deleted || u.status === "Inactive");
        return users.filter((u) => u.status === statusFilter && !u.is_deleted);
    }, [users, statusFilter]);
    const stats = useMemo(() => ({
        total: users.filter((u) => !u.is_deleted).length,
        pending: users.filter((u) => u.status === "Pending" && !u.is_deleted).length,
        active: users.filter((u) => u.status === "Active" && !u.is_deleted).length,
        suspended: users.filter((u) => u.status === "Suspended" && !u.is_deleted).length,
        inactive: users.filter((u) => u.is_deleted || u.status === "Inactive").length,
    }), [users]);

    const approve = async (u) => {
        setBusy(u.id);
        try {
            await api.post(`/admin/users/${u.id}/approve`);
            toast.success(`${u.email} approved`);
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally { setBusy(null); }
    };
    const suspend = async (u) => {
        if (!window.confirm(`Suspend ${u.email}? They will be denied access until reactivated.`)) return;
        setBusy(u.id);
        try {
            await api.post(`/admin/users/${u.id}/suspend`);
            toast.success(`${u.email} suspended`);
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally { setBusy(null); }
    };
    const [deleteTarget, setDeleteTarget] = useState(null);
    const softDelete = async () => {
        if (!deleteTarget) return;
        setBusy(deleteTarget.id);
        try {
            await api.post(`/admin/users/${deleteTarget.id}/soft-delete`);
            toast.success(`${deleteTarget.email} marked Inactive`);
            setDeleteTarget(null);
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally { setBusy(null); }
    };
    const [restoreTarget, setRestoreTarget] = useState(null);
    const [restoreResult, setRestoreResult] = useState(null);
    const restore = async () => {
        if (!restoreTarget) return;
        setBusy(restoreTarget.id);
        try {
            const { data } = await api.post(`/admin/users/${restoreTarget.id}/restore`);
            setRestoreResult(data);
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
            setRestoreTarget(null);
        } finally { setBusy(null); }
    };
    const changeRole = async (u, role) => {
        setBusy(u.id);
        try {
            await api.patch(`/admin/users/${u.id}`, { role });
            toast.success(`${u.email} → ${role}`);
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally { setBusy(null); }
    };
    const resetPassword = async () => {
        if (!resetTarget) return;
        setBusy(resetTarget.id);
        try {
            const { data } = await api.post(`/admin/users/${resetTarget.id}/reset-password`);
            setTempPwd(data);
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
            setResetTarget(null);
        } finally { setBusy(null); }
    };

    if (loading) return <div className="text-snow-dim font-mono text-sm">Loading users…</div>;

    return (
        <div className="space-y-4" data-testid="users-tab">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Stat testid="users-stat-total" label="Active Users" value={stats.total} />
                <Stat testid="users-stat-pending" label="Pending" value={stats.pending} alert={stats.pending > 0} />
                <Stat testid="users-stat-active" label="Active" value={stats.active} />
                <Stat testid="users-stat-suspended" label="Suspended" value={stats.suspended} />
                <Stat testid="users-stat-inactive" label="Inactive" value={stats.inactive} />
            </div>

            <div className="flex border border-[#2A2A2A] flex-wrap" data-testid="users-status-filter">
                {["All", "Pending", "Active", "Suspended", "Inactive"].map((s) => (
                    <button
                        key={s}
                        data-testid={`users-filter-${s.toLowerCase()}`}
                        onClick={() => setStatusFilter(s)}
                        className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider transition-colors ${
                            statusFilter === s ? "bg-signal text-jet" : "text-snow-dim hover:text-snow"
                        }`}
                    >{s}</button>
                ))}
            </div>

            <div className="border border-[#2A2A2A] bg-jet-elev1 overflow-x-auto">
                <table className="w-full" data-testid="users-table">
                    <thead><tr className="border-b border-[#2A2A2A]">
                        <Th>Email</Th><Th>Name</Th><Th>Role</Th><Th>Status</Th><Th>Actions</Th>
                    </tr></thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan={5} className="px-5 py-8 text-center text-snow-muted text-xs font-mono">No users match this filter.</td></tr>
                        ) : filtered.map((u) => (
                            <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-[#2A2A2A] hover:bg-white/[0.03]">
                                <Td><span className="font-mono text-sm">{u.email}</span></Td>
                                <Td>
                                    <span className="font-mono text-sm text-snow-dim">
                                        {u.name}
                                        {u.is_deleted && (
                                            <span className="ml-2 text-red-400 text-[10px] uppercase tracking-wider" data-testid={`inactive-suffix-${u.email}`}>
                                                (Inactive)
                                            </span>
                                        )}
                                    </span>
                                    {u.job_title && (
                                        <div className="text-[10px] text-snow-muted font-mono mt-0.5">{u.job_title}</div>
                                    )}
                                </Td>
                                <Td>
                                    <select
                                        data-testid={`user-role-select-${u.email}`}
                                        value={u.role}
                                        disabled={busy === u.id || u.is_deleted || (u.role === "super_admin" && !perm.isSuperAdmin)}
                                        onChange={(e) => changeRole(u, e.target.value)}
                                        className="bg-jet border border-[#2A2A2A] text-snow font-mono text-xs px-2 py-1 focus:border-signal outline-none disabled:opacity-60"
                                    >
                                        {roles.map((r) => (
                                            <option key={r.name} value={r.name} disabled={r.name === "super_admin" && !perm.isSuperAdmin}>
                                                {ROLE_LABELS[r.name] || r.name}
                                            </option>
                                        ))}
                                    </select>
                                    {u.role === "super_admin" && (
                                        <span className="ml-2 inline-flex items-center border bg-signal text-jet font-bold px-2 py-0.5 text-[9px] uppercase tracking-[0.2em]" data-testid={`super-admin-badge-${u.email}`}>
                                            Super Admin
                                        </span>
                                    )}
                                </Td>
                                <Td><StatusBadge status={u.status} /></Td>
                                <Td>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        {u.status === "Pending" && !u.is_deleted && (
                                            <button
                                                data-testid={`approve-${u.email}`}
                                                onClick={() => approve(u)}
                                                disabled={busy === u.id}
                                                className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-signal text-signal hover:bg-signal hover:text-jet transition-colors"
                                            >
                                                {busy === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Approve"}
                                            </button>
                                        )}
                                        {u.status !== "Suspended" && u.status !== "Inactive" && !u.is_deleted && (
                                            <button
                                                data-testid={`suspend-${u.email}`}
                                                onClick={() => suspend(u)}
                                                disabled={busy === u.id || (u.role === "super_admin" && !perm.isSuperAdmin)}
                                                className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-[#2A2A2A] text-snow-dim hover:border-red-500 hover:text-red-400 transition-colors"
                                            >
                                                Suspend
                                            </button>
                                        )}
                                        <button
                                            data-testid={`reset-pwd-${u.email}`}
                                            onClick={() => { setResetTarget(u); setTempPwd(null); }}
                                            disabled={busy === u.id || u.is_deleted}
                                            className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-[#2A2A2A] text-snow-dim hover:border-snow hover:text-snow transition-colors flex items-center gap-1 disabled:opacity-40"
                                        >
                                            <KeyRound className="w-3 h-3" /> Reset
                                        </button>
                                        {/* Soft-Delete (Trash) — visible ONLY for Suspended users per Build Prompt #15 */}
                                        {u.status === "Suspended" && !u.is_deleted && (
                                            <button
                                                data-testid={`soft-delete-${u.email}`}
                                                onClick={() => setDeleteTarget(u)}
                                                disabled={busy === u.id || (u.role === "super_admin" && !perm.isSuperAdmin)}
                                                className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-red-500/60 text-red-400 hover:bg-red-500/10 hover:border-red-500 transition-colors flex items-center gap-1"
                                                title="Soft-delete user (referential integrity preserved)"
                                            >
                                                <Trash2 className="w-3 h-3" /> Delete
                                            </button>
                                        )}
                                        {/* Restore — visible ONLY for soft-deleted (Inactive) users */}
                                        {u.is_deleted && (
                                            <button
                                                data-testid={`restore-${u.email}`}
                                                onClick={() => { setRestoreTarget(u); setRestoreResult(null); }}
                                                disabled={busy === u.id}
                                                className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-signal text-signal hover:bg-signal hover:text-jet transition-colors flex items-center gap-1"
                                                title="Restore user with a fresh temporary password"
                                            >
                                                <RotateCcw className="w-3 h-3" /> Restore
                                            </button>
                                        )}
                                    </div>
                                </Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {resetTarget && (
                <Modal
                    title={tempPwd ? "Temporary Password Generated" : `Reset password for ${resetTarget.email}?`}
                    onClose={() => { setResetTarget(null); setTempPwd(null); }}
                    accent
                >
                    {!tempPwd ? (
                        <div className="space-y-4" data-testid="reset-confirm-modal">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="w-6 h-6 text-signal flex-shrink-0 mt-1" />
                                <div className="text-sm text-snow-dim font-mono">
                                    A new 12-character temporary password will be generated. The user's current password will be invalidated immediately. The temp password will be shown ONCE — copy it before closing the modal.
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                                <button onClick={() => setResetTarget(null)} className="cerb-btn-secondary" data-testid="reset-cancel">Cancel</button>
                                <button onClick={resetPassword} disabled={busy === resetTarget.id} className="cerb-btn-primary" data-testid="reset-confirm">
                                    {busy === resetTarget.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate Temp Password"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4" data-testid="reset-result-modal">
                            <div className="border border-signal bg-signal/5 px-3 py-2 text-[11px] font-mono text-signal flex items-start gap-2">
                                <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                <span><span className="font-bold">SHOWN ONCE.</span> Copy this password and share it through a secure channel. Closing this modal will discard it.</span>
                            </div>
                            <div className="border border-[#2A2A2A] bg-jet p-4 text-center font-mono">
                                <div className="overline mb-2">Temporary Password for {tempPwd.email}</div>
                                <div className="text-2xl text-signal tabular-nums tracking-wider select-all" data-testid="temp-password-value">{tempPwd.temporary_password}</div>
                            </div>
                            <CopyButton value={tempPwd.temporary_password} testId="copy-temp-password" />
                            <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                                <button onClick={() => { setResetTarget(null); setTempPwd(null); }} className="cerb-btn-primary" data-testid="reset-close">Done</button>
                            </div>
                        </div>
                    )}
                </Modal>
            )}

            {/* Soft-Delete confirmation — Alert Red theme */}
            {deleteTarget && (
                <Modal
                    title={`Soft-Delete ${deleteTarget.email}?`}
                    onClose={() => setDeleteTarget(null)}
                    danger
                >
                    <div className="space-y-4" data-testid="soft-delete-modal">
                        <div className="border border-red-500/60 bg-red-500/10 px-4 py-3 flex items-start gap-3">
                            <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                            <div className="text-sm font-mono">
                                <div className="text-red-400 font-bold uppercase tracking-wider text-xs mb-1">Soft Delete (Reversible)</div>
                                <div className="text-snow-dim">
                                    <span className="text-snow">{deleteTarget.email}</span> will be marked
                                    <span className="text-red-400"> Inactive</span>. Their password will be scrambled
                                    and all active JWT sessions revoked immediately. The user record stays in the
                                    database so all linked product ideas, approvals, and audit logs remain valid —
                                    their name will display as <span className="text-snow">"{deleteTarget.name} (Inactive)"</span>.
                                    You can restore them later from the <span className="text-snow">Inactive</span> tab.
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button onClick={() => setDeleteTarget(null)} className="cerb-btn-secondary" data-testid="soft-delete-cancel">Cancel</button>
                            <button
                                onClick={softDelete}
                                disabled={busy === deleteTarget.id}
                                className="bg-red-500 hover:bg-red-600 text-jet font-bold uppercase tracking-[0.2em] px-5 py-2.5 text-xs flex items-center gap-2 transition-colors"
                                data-testid="soft-delete-confirm"
                            >
                                {busy === deleteTarget.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-3.5 h-3.5" /> Confirm Delete</>}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Restore confirmation + temp-password result */}
            {restoreTarget && (
                <Modal
                    title={restoreResult ? "Temp Password Generated" : `Restore ${restoreTarget.email}?`}
                    onClose={() => { setRestoreTarget(null); setRestoreResult(null); }}
                    accent
                >
                    {!restoreResult ? (
                        <div className="space-y-4" data-testid="restore-confirm-modal">
                            <div className="border border-signal bg-signal/5 px-4 py-3 flex items-start gap-3">
                                <RotateCcw className="w-5 h-5 text-signal flex-shrink-0 mt-1" />
                                <div className="text-sm text-snow-dim font-mono">
                                    Restoring <span className="text-snow">{restoreTarget.email}</span> will set their status to
                                    <span className="text-signal"> Suspended</span> and issue a fresh 12-character temporary
                                    password (shown ONCE). An admin must manually approve them back to Active afterward.
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                                <button onClick={() => setRestoreTarget(null)} className="cerb-btn-secondary" data-testid="restore-cancel">Cancel</button>
                                <button onClick={restore} disabled={busy === restoreTarget.id} className="cerb-btn-primary" data-testid="restore-confirm">
                                    {busy === restoreTarget.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Restore + Issue Temp Password"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4" data-testid="restore-result-modal">
                            <div className="border border-signal bg-signal/5 px-3 py-2 text-[11px] font-mono text-signal flex items-start gap-2">
                                <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                <span><span className="font-bold">SHOWN ONCE.</span> Copy this password and share it through a secure channel.</span>
                            </div>
                            <div className="border border-[#2A2A2A] bg-jet p-4 text-center font-mono">
                                <div className="overline mb-2">Temporary Password for {restoreResult.email}</div>
                                <div className="text-2xl text-signal tabular-nums tracking-wider select-all" data-testid="restore-temp-password-value">{restoreResult.temporary_password}</div>
                            </div>
                            <CopyButton value={restoreResult.temporary_password} testId="copy-restore-password" />
                            <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                                <button onClick={() => { setRestoreTarget(null); setRestoreResult(null); }} className="cerb-btn-primary" data-testid="restore-close">Done</button>
                            </div>
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
}

// ===== INVITATIONS =====
function InvitationsTab() {
    const [invites, setInvites] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ email: "", role_to_assign: "mission_partner" });
    const [busy, setBusy] = useState(false);
    const [createdInvite, setCreatedInvite] = useState(null);

    const refresh = async () => {
        try {
            const [i, r] = await Promise.all([
                api.get("/admin/invitations").then((r) => r.data),
                api.get("/admin/roles").then((r) => r.data).catch(() => []),
            ]);
            setInvites(i);
            setRoles(r);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { refresh(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/admin/invitations", form);
            setCreatedInvite(data);
            setCreating(false);
            setForm({ email: "", role_to_assign: "mission_partner" });
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally { setBusy(false); }
    };
    const revoke = async (inv) => {
        if (!window.confirm(`Revoke invitation for ${inv.email}?`)) return;
        try {
            await api.delete(`/admin/invitations/${inv.id}`);
            toast.success("Invitation revoked");
            refresh();
        } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message); }
    };

    const inviteUrl = (token) => `${window.location.origin}/register?invite=${token}`;

    if (loading) return <div className="text-snow-dim font-mono text-sm">Loading invitations…</div>;

    return (
        <div className="space-y-4" data-testid="invitations-tab">
            <div className="flex items-center justify-between">
                <div className="overline">Active &amp; Past Invitations ({invites.length})</div>
                <button data-testid="create-invite-btn" onClick={() => setCreating(true)} className="bg-signal text-jet hover:bg-signal-hover px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] flex items-center gap-2">
                    <Plus className="w-3 h-3" /> New Invitation
                </button>
            </div>
            <div className="border border-[#2A2A2A] bg-jet-elev1 overflow-x-auto">
                <table className="w-full" data-testid="invitations-table">
                    <thead><tr className="border-b border-[#2A2A2A]">
                        <Th>Email</Th><Th>Role</Th><Th>Status</Th><Th>Created</Th><Th>Expires</Th><Th>Actions</Th>
                    </tr></thead>
                    <tbody>
                        {invites.length === 0 ? (
                            <tr><td colSpan={6} className="px-5 py-8 text-center text-snow-muted text-xs font-mono">No invitations yet.</td></tr>
                        ) : invites.map((inv) => {
                            const isUsed = !!inv.used_at;
                            const isExpired = !isUsed && new Date(inv.expires_at) < new Date();
                            return (
                                <tr key={inv.id} data-testid={`invite-row-${inv.email}`} className="border-b border-[#2A2A2A]">
                                    <Td><span className="font-mono text-sm">{inv.email}</span></Td>
                                    <Td><span className="font-mono text-xs text-snow-dim">{ROLE_LABELS[inv.role_to_assign] || inv.role_to_assign}</span></Td>
                                    <Td>
                                        {isUsed
                                            ? <span className="inline-flex items-center border border-green-500/40 text-green-400 bg-green-500/5 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono">Used</span>
                                            : isExpired
                                            ? <span className="inline-flex items-center border border-[#2A2A2A] text-snow-muted px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono">Expired</span>
                                            : <span className="inline-flex items-center border border-signal text-signal bg-signal/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono">Active</span>
                                        }
                                    </Td>
                                    <Td><span className="text-[11px] font-mono text-snow-muted tabular-nums">{relativeTime(inv.created_at)}</span></Td>
                                    <Td><span className="text-[11px] font-mono text-snow-muted tabular-nums">{relativeTime(inv.expires_at)}</span></Td>
                                    <Td>
                                        <div className="flex items-center gap-1.5">
                                            {!isUsed && !isExpired && (
                                                <CopyButton value={inviteUrl(inv.token)} testId={`copy-invite-${inv.email}`} small />
                                            )}
                                            <button
                                                data-testid={`revoke-invite-${inv.email}`}
                                                onClick={() => revoke(inv)}
                                                disabled={isUsed}
                                                className="p-1.5 border border-[#2A2A2A] text-snow-muted hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-30"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </Td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {creating && (
                <Modal title="New Invitation" onClose={() => setCreating(false)}>
                    <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
                        <Field label="Email">
                            <input data-testid="invite-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="cerb-input" placeholder="newuser@org.com" />
                        </Field>
                        <Field label="Role to Assign on Approval">
                            <select data-testid="invite-role-select" value={form.role_to_assign} onChange={(e) => setForm({ ...form, role_to_assign: e.target.value })} className="cerb-input">
                                {roles
                                    // Build Prompt #15 — Super Admin cannot be invited via the standard flow.
                                    .filter((r) => r.name !== "super_admin")
                                    .map((r) => (
                                        <option key={r.name} value={r.name}>
                                            {ROLE_LABELS[r.name] || r.name}
                                        </option>
                                    ))}
                            </select>
                        </Field>
                        <div className="text-[11px] text-snow-muted font-mono">
                            Link expires in 7 days. The invitee registers, lands in <span className="text-signal">Pending</span>, and an admin must approve before they can access data.
                        </div>
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button type="button" onClick={() => setCreating(false)} className="cerb-btn-secondary">Cancel</button>
                            <button type="submit" disabled={busy} className="cerb-btn-primary" data-testid="invite-submit">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate Invitation"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {createdInvite && (
                <Modal title="Invitation Generated" onClose={() => setCreatedInvite(null)} accent>
                    <div className="space-y-4" data-testid="invite-success-modal">
                        <div className="border border-signal bg-signal/5 px-3 py-2 text-[11px] font-mono text-signal flex items-start gap-2">
                            <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                            <span>Copy this link and share it via your trusted channel. The token is single-use and expires in 7 days.</span>
                        </div>
                        <div className="border border-[#2A2A2A] bg-jet p-3 font-mono text-xs text-signal break-all" data-testid="invite-link-display">{inviteUrl(createdInvite.token)}</div>
                        <CopyButton value={inviteUrl(createdInvite.token)} testId="copy-new-invite" />
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button onClick={() => setCreatedInvite(null)} className="cerb-btn-primary">Done</button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// ===== ROLES & PERMISSIONS =====
function RolesTab() {
    const [roles, setRoles] = useState([]);
    const [flags, setFlags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [editPerms, setEditPerms] = useState(new Set());
    const [form, setForm] = useState({ name: "", description: "", permissions: new Set() });
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        try {
            const [r, f] = await Promise.all([
                api.get("/admin/roles").then((r) => r.data),
                api.get("/admin/permission-flags").then((r) => r.data.flags),
            ]);
            setRoles(r);
            setFlags(f);
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    const togglePerm = (set, p) => {
        const next = new Set(set);
        if (next.has(p)) next.delete(p);
        else next.add(p);
        return next;
    };

    const submitNew = async (e) => {
        e.preventDefault();
        if (!/^[a-z][a-z0-9_]*$/.test(form.name)) {
            toast.error("Role name must be lowercase letters/digits/underscores, starting with a letter.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/admin/roles", { name: form.name, description: form.description, permissions: Array.from(form.permissions) });
            toast.success(`Role '${form.name}' created`);
            setCreating(false);
            setForm({ name: "", description: "", permissions: new Set() });
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally { setBusy(false); }
    };
    const saveEdit = async () => {
        if (!editTarget) return;
        setBusy(true);
        try {
            await api.patch(`/admin/roles/${editTarget.id}`, { permissions: Array.from(editPerms) });
            toast.success(`'${editTarget.name}' updated`);
            setEditTarget(null);
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally { setBusy(false); }
    };
    const deleteRole = async (r) => {
        if (!window.confirm(`Delete role '${r.name}'? Users assigned to it must be reassigned first.`)) return;
        try {
            await api.delete(`/admin/roles/${r.id}`);
            toast.success("Role deleted");
            refresh();
        } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message); }
    };

    if (loading) return <div className="text-snow-dim font-mono text-sm">Loading roles…</div>;

    return (
        <div className="space-y-4" data-testid="roles-tab">
            <div className="flex items-center justify-between">
                <div className="overline">Roles ({roles.length})</div>
                <button data-testid="create-role-btn" onClick={() => setCreating(true)} className="bg-signal text-jet hover:bg-signal-hover px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] flex items-center gap-2">
                    <Plus className="w-3 h-3" /> New Role
                </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {roles.map((r) => (
                    <div key={r.id} data-testid={`role-card-${r.name}`} className={`border ${r.is_built_in ? "border-[#2A2A2A]" : "border-signal/40"} bg-jet-elev1 p-4 space-y-2`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="font-mono text-sm text-snow uppercase tracking-wider">{r.name}</span>
                                {r.name === "super_admin" && (
                                    <span className="inline-flex items-center border bg-signal text-jet font-bold px-2 py-0.5 text-[9px] uppercase tracking-[0.2em]" data-testid="super-admin-role-badge">SUPER ADMIN</span>
                                )}
                                {r.is_built_in && (
                                    <span className="inline-flex items-center border border-[#2A2A2A] text-snow-muted px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] font-mono">Built-in</span>
                                )}
                            </div>
                            {!r.is_built_in && (
                                <div className="flex items-center gap-1">
                                    <button
                                        data-testid={`edit-role-${r.name}`}
                                        onClick={() => { setEditTarget(r); setEditPerms(new Set(r.permissions)); }}
                                        className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-[#2A2A2A] text-snow-dim hover:border-signal hover:text-signal"
                                    >Edit</button>
                                    <button
                                        data-testid={`delete-role-${r.name}`}
                                        onClick={() => deleteRole(r)}
                                        className="p-1 border border-[#2A2A2A] text-snow-muted hover:border-red-500 hover:text-red-400"
                                    ><Trash2 className="w-3 h-3" /></button>
                                </div>
                            )}
                        </div>
                        <div className="text-[11px] text-snow-dim font-mono">{r.description || "—"}</div>
                        <div className="flex flex-wrap gap-1">
                            {r.permissions.slice(0, 12).map((p) => (
                                <span key={p} className="text-[9px] font-mono border border-[#2A2A2A] text-snow-muted px-1.5 py-0.5 lowercase">{p}</span>
                            ))}
                            {r.permissions.length > 12 && (
                                <span className="text-[9px] font-mono text-snow-muted">+{r.permissions.length - 12} more</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {creating && (
                <Modal title="Create Custom Role" onClose={() => setCreating(false)}>
                    <form onSubmit={submitNew} className="space-y-4" data-testid="role-form">
                        <Field label="Role Name (lowercase, no spaces)">
                            <input data-testid="role-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase() })} className="cerb-input" placeholder="auditor" />
                        </Field>
                        <Field label="Description">
                            <input data-testid="role-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="cerb-input" placeholder="Read-only audit & compliance reviewer." />
                        </Field>
                        <Field label="Permissions">
                            <div className="grid grid-cols-2 gap-1.5 max-h-72 overflow-y-auto border border-[#2A2A2A] p-2" data-testid="role-perms-grid">
                                {flags.map((p) => (
                                    <label key={p} className="flex items-center gap-2 text-[11px] font-mono cursor-pointer hover:bg-white/5 px-2 py-1">
                                        <input type="checkbox" checked={form.permissions.has(p)} onChange={() => setForm({ ...form, permissions: togglePerm(form.permissions, p) })} className="accent-[#FF6A00]" data-testid={`role-perm-${p}`} />
                                        <span className="text-snow-dim">{p}</span>
                                    </label>
                                ))}
                            </div>
                        </Field>
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button type="button" onClick={() => setCreating(false)} className="cerb-btn-secondary">Cancel</button>
                            <button type="submit" disabled={busy} className="cerb-btn-primary" data-testid="role-submit">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Role"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {editTarget && (
                <Modal title={`Edit '${editTarget.name}' permissions`} onClose={() => setEditTarget(null)}>
                    <div className="space-y-4" data-testid="role-edit-modal">
                        <div className="grid grid-cols-2 gap-1.5 max-h-72 overflow-y-auto border border-[#2A2A2A] p-2">
                            {flags.map((p) => (
                                <label key={p} className="flex items-center gap-2 text-[11px] font-mono cursor-pointer hover:bg-white/5 px-2 py-1">
                                    <input type="checkbox" checked={editPerms.has(p)} onChange={() => setEditPerms((s) => togglePerm(s, p))} className="accent-[#FF6A00]" data-testid={`edit-perm-${p}`} />
                                    <span className="text-snow-dim">{p}</span>
                                </label>
                            ))}
                        </div>
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button onClick={() => setEditTarget(null)} className="cerb-btn-secondary">Cancel</button>
                            <button onClick={saveEdit} disabled={busy} className="cerb-btn-primary" data-testid="edit-role-save">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// ===== SYSTEM HEALTH =====
function HealthTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/admin/system-health").then((r) => setData(r.data)).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-snow-dim font-mono text-sm">Loading system health…</div>;
    if (!data) return null;

    return (
        <div className="space-y-6" data-testid="health-tab">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat testid="health-stat-killswitch" label="Active Kill Switches" value={data.kill_switches_count} alert={data.kill_switches_count > 0} />
                <Stat testid="health-stat-pending-users" label="Pending Users" value={data.pending_user_count} alert={data.pending_user_count > 0} />
                <Stat testid="health-stat-open-invites" label="Open Invitations" value={data.open_invitation_count} />
                <Stat testid="health-stat-audits" label="Recent Audit Events" value={data.recent_audits.length} />
            </div>

            {data.kill_switches_active.length > 0 && (
                <div className="border-2 border-signal bg-signal/5" data-testid="kill-switches-panel">
                    <div className="border-b border-signal/40 px-5 py-3 flex items-center gap-3 bg-signal/10">
                        <Power className="w-4 h-4 text-signal animate-pulse-signal" />
                        <span className="overline text-signal">Active Kill Switches Across All Products</span>
                    </div>
                    <ul className="divide-y divide-signal/20">
                        {data.kill_switches_active.map((p) => (
                            <li key={p.id} data-testid={`killed-product-${p.id}`} className="px-5 py-3 flex items-center gap-3 text-xs font-mono">
                                <span className="text-signal font-bold">[GROUNDED]</span>
                                <Link to={`/products/${p.id}`} className="text-snow hover:text-signal underline-offset-2 hover:underline">{p.name}</Link>
                                <span className="text-snow-muted">→ {p.lifecycle_status} · {p.risk_tier}</span>
                                {p.active_recertification && <span className="text-signal">· Recert {p.recertification_scope}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="border border-[#2A2A2A] bg-jet-elev1" data-testid="recent-audits-panel">
                <div className="border-b border-[#2A2A2A] px-5 py-3 flex items-center gap-3">
                    <Activity className="w-3 h-3 text-signal" />
                    <span className="overline">Recent Audit Events ({data.recent_audits.length})</span>
                </div>
                <ul className="divide-y divide-[#2A2A2A] max-h-[600px] overflow-y-auto">
                    {data.recent_audits.map((a) => (
                        <li key={a.id} data-testid={`audit-row-${a.id}`} className="px-5 py-2 flex items-start gap-3 text-[11px] font-mono">
                            <span className="text-snow-muted tabular-nums whitespace-nowrap">{relativeTime(a.timestamp)}</span>
                            <span className="text-signal whitespace-nowrap">[{a.action_type}]</span>
                            <span className="text-snow-dim">{a.operator_email}</span>
                            <span className="text-snow truncate">→ {a.target_label || a.target_id}</span>
                            {a.reason && <span className="text-snow-muted italic truncate">"{a.reason}"</span>}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

// ===== shared sub-components =====
function Stat({ label, value, alert, testid }) {
    return (
        <div data-testid={testid} className={`border ${alert ? "border-signal bg-signal/5" : "border-[#2A2A2A] bg-jet-elev1"} p-4`}>
            <div className="overline">{label}</div>
            <div className={`heading-display text-3xl mt-2 tabular-nums ${alert ? "text-signal" : "text-snow"}`}>{value || 0}</div>
        </div>
    );
}
function Th({ children }) { return <th className="text-left px-5 py-3 overline font-normal">{children}</th>; }
function Td({ children }) { return <td className="px-5 py-3 align-middle">{children}</td>; }
function Field({ label, children }) { return <div><label className="overline block mb-2">{label}</label>{children}</div>; }

function StatusBadge({ status }) {
    const map = {
        Active: "border-green-500/40 text-green-400 bg-green-500/5",
        Pending: "border-signal text-signal bg-signal/10",
        Suspended: "border-red-500/40 text-red-400 bg-red-500/5",
    };
    return <span data-testid={`user-status-${status?.toLowerCase()}`} className={`inline-flex items-center border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono ${map[status] || "border-[#2A2A2A] text-snow-dim"}`}>{status}</span>;
}

function CopyButton({ value, testId, small }) {
    const [copied, setCopied] = useState(false);
    const onClick = async () => {
        try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); }
        catch { toast.error("Could not copy. Select and copy manually."); }
    };
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`${small ? "p-1.5" : "px-3 py-2"} border border-signal text-signal hover:bg-signal hover:text-jet transition-colors flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider`}
        >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {!small && (copied ? "Copied" : "Copy Link")}
        </button>
    );
}

function Modal({ title, children, onClose, accent, danger }) {
    const borderCls = danger ? "border-red-500/60" : accent ? "border-signal" : "border-hairline";
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-jet/80 backdrop-blur-sm p-4" onClick={onClose}>
            <div className={`relative w-full max-w-2xl bg-jet-elev1 border ${borderCls} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
                <div className={`flex items-center justify-between border-b ${borderCls} px-5 py-3`}>
                    <div>
                        <div className={`overline ${danger ? "text-red-400" : ""}`}>{danger ? "Destructive Action" : "Action"}</div>
                        <div className="heading-display text-lg">{title}</div>
                    </div>
                    <button onClick={onClose} className="text-snow-dim hover:text-signal" data-testid="modal-close-btn"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}

function relativeTime(iso) {
    try {
        const diff = (Date.now() - new Date(iso).getTime()) / 1000;
        if (diff < 60) return `${Math.floor(diff)}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    } catch { return iso; }
}
